import { useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, CheckCheck, MailOpen } from "lucide-react";
import { toast } from "sonner";
import {
  NOTIFICATION_TYPES,
  getNotificationsForUser,
  getLeaveRequestById,
  getSessionUser,
  markNotificationRead,
  resolveLeaveRequest,
} from "@/lib/eisStore";

export default function Notifications() {
  const user = getSessionUser();
  const userKey = `${user?.role || "user"}:${user?.email || user?.id || user?.name || ""}`;
  const [refreshTick, setRefreshTick] = useState(0);
  const [expandedNotificationId, setExpandedNotificationId] = useState("");
  const notifications = getNotificationsForUser(user || {});
  const unread = notifications.filter((item) => !(item.readBy || []).includes(userKey));
  const isManagement = user?.role === "management";

  const handleLeaveDecision = (requestId, decision, notificationId) => {
    resolveLeaveRequest(requestId, decision);
    markNotificationRead(notificationId, user);
    setRefreshTick((current) => current + 1);
    toast.success(`Leave request ${decision}`);
  };

  const markAll = () => {
    notifications.forEach((notification) => markNotificationRead(notification.id, user));
    setRefreshTick((current) => current + 1);
    toast.success("All notifications marked as read");
  };

  const renderDetailRows = (metadata = {}) => {
    const rows = [];
    if (metadata.studentName) rows.push(["Student", metadata.studentName]);
    if (metadata.standard || metadata.division) {
      rows.push(["Class", `${metadata.standard || ""} ${metadata.division || ""}`.trim()]);
    }
    if (metadata.remark) rows.push(["Remark", metadata.remark]);
    if (Array.isArray(metadata.changes) && metadata.changes.length) {
      metadata.changes.forEach((change) => {
        rows.push([
          change.label || change.field,
          `${change.oldValue || "N/A"} -> ${change.newValue || "N/A"}`,
        ]);
      });
    }
    if (metadata.field && metadata.newValue && !metadata.changes?.length) {
      rows.push([metadata.field, `${metadata.oldValue || "N/A"} -> ${metadata.newValue}`]);
    }
    return rows;
  };

  return (
    <AppShell>
      <div data-testid="notifications-page" className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
              Notifications
            </h2>
            <p className="text-slate-500 mt-1 text-sm">
              Updates from teachers, admin actions, and school alerts.
            </p>
          </div>
          {notifications.length > 0 && (
            <Button
              type="button"
              onClick={markAll}
              className="rounded-full text-white"
              style={{ backgroundColor: "#0b1f7a" }}
            >
              <CheckCheck size={16} className="mr-2" />
              Mark all read
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle style={{ color: "#0b1f7a" }}>
              {unread.length} unread
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {notifications.length ? (
              notifications.map((notification) => {
                const isRead = (notification.readBy || []).includes(
                  `${user?.role || "user"}:${user?.email || user?.id || user?.name || ""}`,
                );
                const isExpanded = expandedNotificationId === notification.id;
                return (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      markNotificationRead(notification.id, user);
                      setExpandedNotificationId((current) =>
                        current === notification.id ? "" : notification.id,
                      );
                      setRefreshTick((current) => current + 1);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        markNotificationRead(notification.id, user);
                        setExpandedNotificationId((current) =>
                          current === notification.id ? "" : notification.id,
                        );
                        setRefreshTick((current) => current + 1);
                      }
                    }}
                    className={`w-full text-left rounded-xl border p-4 transition ${
                      isRead ? "border-slate-200 bg-white" : "border-[#0b1f7a] bg-[#EEF0FA]"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: "#0b1f7a" }}
                      >
                        <Bell size={18} className="text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-semibold text-slate-800">{notification.title}</p>
                          {!isRead && <Badge>New</Badge>}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                        <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                          <span>{new Date(notification.createdAt).toLocaleString()}</span>
                          <span className="capitalize">{notification.type.replace(/_/g, " ")}</span>
                        </div>
                        {isExpanded && (
                          <div className="mt-3 rounded-xl bg-slate-50 p-4 space-y-2 text-xs text-slate-600">
                            {renderDetailRows(notification.metadata).length ? (
                              renderDetailRows(notification.metadata).map(([label, value]) => (
                                <div key={`${notification.id}-${label}-${value}`} className="flex gap-2">
                                  <span className="font-semibold text-slate-700 min-w-20">{label}:</span>
                                  <span className="break-words">{value}</span>
                                </div>
                              ))
                            ) : (
                              <p>No additional details available.</p>
                            )}
                          </div>
                        )}
                        {notification.type === NOTIFICATION_TYPES.remark && !isExpanded && (
                          <p className="text-xs text-slate-500 mt-1">
                            Open to view the remark details.
                          </p>
                        )}
                        {notification.type === "leave_request" && notification.metadata?.requestId && (
                          <div className="mt-3 space-y-2">
                            {(() => {
                              const request = getLeaveRequestById(notification.metadata.requestId);
                              if (!request) return null;
                              return (
                                <>
                                  <p className="text-xs text-slate-500">
                                    {request.fromDate} to {request.toDate} · {request.reason}
                                  </p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                      className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                                        request.status === "approved"
                                          ? "bg-green-100 text-green-700"
                                          : request.status === "rejected"
                                            ? "bg-red-100 text-red-700"
                                            : "bg-amber-100 text-amber-700"
                                      }`}
                                    >
                                      {request.status}
                                    </span>
                                    {isManagement && request.status === "pending" && (
                                      <>
                                        <Button
                                          type="button"
                                          size="sm"
                                          className="rounded-full text-white"
                                          style={{ backgroundColor: "#16a34a" }}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleLeaveDecision(request.id, "approved", notification.id);
                                          }}
                                        >
                                          Approve
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleLeaveDecision(request.id, "rejected", notification.id);
                                          }}
                                        >
                                          Reject
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
                <MailOpen size={32} className="text-slate-400" />
                <p className="text-lg font-semibold text-slate-700 mt-4">
                  No notifications yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
